import { useState, useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import ImageLinks from "../../assets/Images/ImagesLink";
import { Icon } from "leaflet";
import _ from "lodash";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api_requests from "../../resources/api_requests";
import moment from "moment";
import L from "leaflet";
import OrderCancelConfirm from "../../modules/modals/OrderCancelConfirm";
import utils from "../../resources/utils";
import "leaflet-routing-machine";
import { useDispatch, useSelector } from "react-redux";
import { hide_loader, show_loader } from "../../actions/app";
import toast from "react-hot-toast";
import { CallCircle } from "../../assets/whiteLabelIcon";
import constant from "../../resources/constant";
import { Share } from "@capacitor/share";
import {
  convertUTCToTimezone,
  formatCurrencyCountry,
} from "../../resources/internationalizationUtils";
import { useTranslation } from "react-i18next";

const icon_rider = new Icon({ iconUrl: ImageLinks.rider, iconSize: [30, 30] });
const icon_drop = new Icon({
  iconUrl: ImageLinks.drop,
  iconSize: [30, 30],
});
const icon_pick = new Icon({
  iconUrl: ImageLinks.pickup,
  iconSize: [30, 30],
});

const VIEW_MODE = {
  DETAILS: "details",
  TIMELINE: "timeline",
};
const pre_pickup_status = ["OUT_FOR_PICKUP", "CREATED", "REACHED_PICKUP"];

const OrderTrackDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let { order_id } = useParams();
  const [searchParams] = useSearchParams();
  const route_from = searchParams.get("from");
  const [order_detail, set_order_detail] = useState(null);
  const [rider_location, set_rider_location] = useState({});
  const [awb_copied, set_awb_copied] = useState(false);
  const [ref_copied, set_ref_copied] = useState(false);
  const [show_cancel_confirm_modal, set_show_cancel_confirm_modal] =
    useState(false);
  const [view_mode, set_view_mode] = useState(VIEW_MODE.DETAILS);
  const [timeline, set_timeline] = useState([]);
  const [price_summary, set_price_summary] = useState({});
  const [show_price_breakdown, set_show_price_breakdown] = useState(false);
  const [platform_fees, set_platform_fees] = useState(5);
  const [delivery_charge, set_delivery_charge] = useState(null);
  const userDetails = useSelector((state) => state.app.user_details);
  const currency_code = userDetails?.currency || "DEFAULT";
  const tz = userDetails?.business_timezone || "DEFAULT";
  const { t } = useTranslation();

  const ONDC_STATUS_TRANSLATION_KEYS = {
    "Agent assigned": "order_status.agent_assigned",
    "Out for delivery": "order_status.out_for_delivery",
    "Order delivered": "order_status.order_delivered",
    "Order picked up": "order_status.order_picked_up",
    "Pending": "order_status.pending",
    "Cancelled": "order_status.cancelled",
    "At pickup": "order_status.at_pickup",
    "At delivery": "order_status.at_delivery",
  };
  useEffect(() => {
    get_order_details(order_id);
  }, [order_id]);

  const get_order_details = async (id) => {
    try {
      dispatch(show_loader());
      const [order_details, pricing, delivery_charge_res] = await Promise.all([
        api_requests.get_order_details(id),
        api_requests.get_pricing(),
        api_requests.get_delivery_charge(id).catch(() => null),
      ]);
      set_order_detail(order_details?.data);
      const pricing_object = pricing?.data?.items?.find(
        (item) => item.feature === order_details?.fulfillment_pricing?.feature,
      )?.plans?.[0]?.price;
      set_platform_fees(pricing_object?.plans[0]?.price ?? 5);
      set_price_summary(order_details?.data?.fulfillment_order?.quote);
      set_delivery_charge(delivery_charge_res?.data || null);
      set_timeline(
        order_details?.data?.fulfillment?.logs ||
          order_details?.data?.fulfillment_histories[0]?.logs ||
          [],
      );
      if (order_details?.data?.status === 3) {
        fetch_rider_location(id);
      } else {
        dispatch(hide_loader());
      }
    } catch (error) {
      dispatch(hide_loader());
    }
  };

  const fetch_rider_location = async (id) => {
    try {
      const response = await api_requests.get_rider_location(id);
      set_rider_location(response.data);
      dispatch(hide_loader());
    } catch (error) {
      dispatch(hide_loader());
    }
  };

  const handleShare = async () => {
    const shareUrl = `${process.env.REACT_APP_TRACK_FE_URL}/?t=${order_detail?.fulfillment?.track_code}`;
    try {
      await Share.share({
        title: t("share_title"),
        text: t("share_text"),
        url: shareUrl,
      });
    } catch (err) {
      alert(t("share_not_supported"));
    }
  };

  const handle_cancel = () => {
    set_show_cancel_confirm_modal(true);
  };

  const handle_back = () => {
    if (route_from == "search_rider") {
      navigate("/dashboard");
    } else {
      navigate(-1);
    }
  };

  function FitBounds({ pickupCoord, dropCoord }) {
    const map = useMap();

    useEffect(() => {
      if (!map) return;

      // Clear existing routing controls on re-renders
      // map.eachLayer((layer) => {
      //   if (layer instanceof L.Routing.Control) {
      //     map?.removeLayer(layer);
      //   }
      // });

      if (
        pickupCoord?.lat &&
        pickupCoord?.lng &&
        dropCoord?.lat &&
        dropCoord?.lng
      ) {
        const routingControl = L.Routing.control({
          waypoints: [
            L.latLng(pickupCoord?.lat, pickupCoord?.lng),
            L.latLng(dropCoord?.lat, dropCoord?.lng),
          ],
          lineOptions: {
            styles: [
              {
                color: getComputedStyle(
                  document.documentElement,
                ).getPropertyValue("--secondary"),
                opacity: 0.8,
                weight: 4,
              },
            ], // Customize polyline style
          },
          show: false, // Hide the instruction panel
          addWaypoints: false, // Disable adding waypoints on the map
          routeWhileDragging: false, // Disable dragging waypoints
          draggableWaypoints: false, // Disable dragging of existing waypoints
          fitSelectedRoutes: false, // Prevent auto-zooming
          showAlternatives: false, // Disable alternate route suggestions
          createMarker: () => null, // Don't show default markers
        }).addTo(map);

        // Adjust map bounds to show the route
        // routingControl.on("routesfound", (e) => {
        //   const route = e.routes[0];
        //   const latLngs = route.getLatLngs(); // Get the LatLngs of the route's polyline
        //   const bounds = L.latLngBounds(latLngs); // Create bounds based on the LatLngs
        //   map.fitBounds(bounds);
        // });

        // return () => {
        //   map.removeControl(routingControl); // Cleanup on component unmount
        // };
      }
    }, [map, pickupCoord?.lat, dropCoord?.lat]);

    return null;
  }
  const is_pre_pickup = useMemo(() => {
    return _.includes(
      pre_pickup_status,
      _.get(order_detail, "fulfillment.status", "-"),
    );
  }, [order_detail]);

  const upper_circle_class = useMemo(() => {
    const status = order_detail?.fulfillment?.status;
    let color = "bg-dark_green";
    let strip_bg_color = "bg-light_green";
    let strip_text_color = "text-dark_green";
    if (
      _.includes(
        [
          constant.ORDER_STATUS.CREATED.id,
          constant.ORDER_STATUS.OUT_FOR_PICKUP.id,
          constant.ORDER_STATUS.REACHED_PICKUP.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-pink";
      strip_bg_color = "bg-light_pink";
      strip_text_color = "text-pink";
    }
    if (
      _.includes(
        [
          constant.ORDER_STATUS.PICKED_UP.id,
          constant.ORDER_STATUS.OUT_FOR_DELIVERY.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-dark_green";
      strip_bg_color = "bg-light_green";
      strip_text_color = "text-dark_green";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FFE3CF]";
      strip_bg_color = "bg-[#FFE3CF]";
      strip_text_color = "text-[#D75800]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_OUT_FOR_DELIVERY.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FCF0DB]";
      strip_bg_color = "bg-[#FCF0DB]";
      strip_text_color = "text-[#FCF0DB]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_DELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#F2C16B]";
      strip_bg_color = "bg-[#FCF0DB]";
      strip_text_color = "text-[#F2C16B]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.CANCELLED.id],
        constant.ORDER_STATUS[status]?.id
          ? constant.ORDER_STATUS[status]?.id
          : 0,
      ) ||
      _.includes(
        [constant.ORDER_STATUS.DISPOSED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#BC0000]";
      strip_bg_color = "bg-[#FFEAEC]";
      strip_text_color = "text-[#BC0000]";
    }

    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#C4C4C4]";
      strip_bg_color = "bg-[#C4C4C4]";
      strip_text_color = "text-white";
    }

    return { color, strip_bg_color, strip_text_color };
  }, [order_detail, view_mode]);

  const inner_circle_class = useMemo(() => {
    const status = order_detail?.fulfillment?.status;
    let color = "bg-dark_green";
    if (
      _.includes(
        [
          constant.ORDER_STATUS.CREATED.id,
          constant.ORDER_STATUS.OUT_FOR_PICKUP.id,
          constant.ORDER_STATUS.REACHED_PICKUP.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-light_pink2";
    }
    if (
      _.includes(
        [
          constant.ORDER_STATUS.PICKED_UP.id,
          constant.ORDER_STATUS.OUT_FOR_DELIVERY.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-light_green";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FFE3CF]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_OUT_FOR_DELIVERY.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#F2C16B]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_DELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#F2C16B]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.CANCELLED.id],
        constant.ORDER_STATUS[status]?.id
          ? constant.ORDER_STATUS[status]?.id
          : 0,
      )
    ) {
      color = "bg-[#BC0000]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#C4C4C4]";
    }

    return color;
  }, [order_detail]);

  const vertical_line_class = useMemo(() => {
    const status = order_detail?.fulfillment?.status;
    let color = "bg-light_green";
    if (
      _.includes(
        [
          constant.ORDER_STATUS.CREATED.id,
          constant.ORDER_STATUS.OUT_FOR_PICKUP.id,
          constant.ORDER_STATUS.REACHED_PICKUP.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-light_pink2";
    }
    if (
      _.includes(
        [
          constant.ORDER_STATUS.PICKED_UP.id,
          constant.ORDER_STATUS.OUT_FOR_DELIVERY.id,
        ],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-light_green";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FFE3CF]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_OUT_FOR_DELIVERY.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FCF0DB]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_DELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#FCF0DB]";
    }
    if (
      _.includes(
        [constant.ORDER_STATUS.CANCELLED.id],
        constant.ORDER_STATUS[status]?.id
          ? constant.ORDER_STATUS[status]?.id
          : 0,
      )
    ) {
      color = "bg-[#FFEAEC]";
    }

    if (
      _.includes(
        [constant.ORDER_STATUS.RTO_UNDELIVERED.id],
        constant.ORDER_STATUS[status]?.id,
      )
    ) {
      color = "bg-[#C4C4C4]";
    }

    return color;
  }, [order_detail]);

  const markers = useMemo(() => {
    const status = order_detail?.fulfillment?.status;
    let marker1 = {
      lat: order_detail?.sender_detail?.address?.latitude,
      lng: order_detail?.sender_detail?.address?.longitude,
    };
    let marker2 = {
      lat: order_detail?.customer_detail?.address?.latitude,
      lng: order_detail?.customer_detail?.address?.longitude,
    };

    if (
      (status == "OUT_FOR_PICKUP" ||
        status == "REACHED_PICKUP" ||
        status == "RTO_OUT_FOR_DELIVERY") &&
      !_.isEmpty(rider_location) &&
      !_.isEmpty(rider_location?.location?.latitude)
    ) {
      marker1 = {
        lat: rider_location?.location?.latitude,
        lng: rider_location?.location?.longitude,
      };
      marker2 = {
        lat: order_detail?.sender_detail?.address?.latitude,
        lng: order_detail?.sender_detail?.address?.longitude,
      };
    }
    if (
      (status == "OUT_FOR_DELIVERY" ||
        status == "REACHED_DELIVERY" ||
        status == "PICKED_UP") &&
      !_.isEmpty(rider_location) &&
      !_.isEmpty(rider_location?.location?.latitude)
    ) {
      marker1 = {
        lat: rider_location?.location?.latitude,
        lng: rider_location?.location?.longitude,
      };
      marker2 = {
        lat: order_detail?.customer_detail?.address?.latitude,
        lng: order_detail?.customer_detail?.address?.longitude,
      };
    }
    return { marker1, marker2 };
  }, [order_detail, rider_location]);

  const is_show_rider_icon = useMemo(() => {
    const status = order_detail?.fulfillment?.status;
    let val = false;
    if (
      status == "OUT_FOR_PICKUP" ||
      status == "REACHED_PICKUP" ||
      status == "OUT_FOR_DELIVERY" ||
      status == "REACHED_DELIVERY" ||
      status == "PICKED_UP" ||
      status == "RTO_OUT_FOR_DELIVERY"
    )
      val = true;

    return val;
  }, [order_detail]);

  const helper_text = useMemo(() => {
    let text = "";
    const status = order_detail?.fulfillment?.status;
    let eta;
    if (status) {
      const check_status = constant.ORDER_STATUS[status]?.id
        ? constant.ORDER_STATUS[status]?.id
        : 0;
      if (
        check_status == constant.ORDER_STATUS.CREATED.id &&
        !_.isEmpty(order_detail?.fulfillment?.channel)
      ) {
        text = t("assigning_rider");
      } else if (check_status == constant.ORDER_STATUS.REACHED_PICKUP.id) {
        text = t("rider_reached_pickup");
      } else if (check_status == constant.ORDER_STATUS.OUT_FOR_PICKUP.id) {
        const pick_eta = order_detail?.fulfillment?.pickup?.eta;
        if (pick_eta) {
          let time = Math.max(0, moment(pick_eta).diff(moment(), "minutes"));
          if (time != 0) {
            eta = time;
          }
        }
        text = t("rider_on_way_pickup") + (eta ? ` ${t("in_minutes", { minutes: eta })}` : "");
      } else if (check_status == constant.ORDER_STATUS.REACHED_DELIVERY.id) {
        text = t("rider_reached_delivery");
      } else if (check_status == constant.ORDER_STATUS.OUT_FOR_DELIVERY.id) {
        const drop_eta = order_detail?.fulfillment?.drop?.eta;
        if (drop_eta) {
          let time = Math.max(0, moment(drop_eta).diff(moment(), "minutes"));
          if (time != 0) {
            eta = time;
          }
        }
        text = t("rider_on_way_delivery") + (eta ? ` ${t("in_minutes", { minutes: eta })}` : "");
      } else if (check_status == constant.ORDER_STATUS.PICKED_UP.id) {
        text = t("rider_has_picked_up_your_order");
      } else if (check_status == constant.ORDER_STATUS.DELIVERED.id) {
        text = t("order_has_been_delivered");
      } else if (
        check_status == constant.ORDER_STATUS.RTO_OUT_FOR_DELIVERY.id
      ) {
        text = t("rider_is_on_the_way_to_return_your_order");
      } else if (check_status == constant.ORDER_STATUS.UNDELIVERED.id) {
        text = t("customer_denied_accepting_the_order");
      } else if (check_status == constant.ORDER_STATUS.RTO_DELIVERED.id) {
        text = t("rider_has_returned_the_order_to_you");
      } else if (check_status == constant.ORDER_STATUS.CANCELLED.id) {
        text = t("this_order_has_been_cancelled");
      } else if (check_status == constant.ORDER_STATUS.RTO_UNDELIVERED.id) {
        text = t("order_could_not_be_delivered_to_the_origin");
      } else if (check_status == constant.ORDER_STATUS.IN_TRANSIT.id) {
        text = t("order_is_on_its_way_for_delivery");
      } else if (check_status == constant.ORDER_STATUS.IN_TRANSIT.id) {
        text = t("order_is_on_its_way_for_delivery");
      } else if (check_status == constant.ORDER_STATUS.DISPOSED.id) {
        text = t("this_order_has_been_disposed");
      }
    } else {
      const check_status = constant.ORDER_STATUS[status]?.id
        ? constant.ORDER_STATUS[status]?.id
        : 0;
      if (check_status == constant.ORDER_STATUS.CANCELLED.id) {
        text = t("this_order_has_been_cancelled");
      }
    }
    return text;
  }, [order_detail]);

  const total_amount =
    delivery_charge?.items?.[0]?.charges?.total_amount ??
    (price_summary?.price || 0) +
      (platform_fees ? platform_fees + platform_fees * 0.18 : 5.9);

  const formatted_total_amount = formatCurrencyCountry(
    total_amount ?? 0,
    currency_code,
  )?.formatedAmount;

  if (!order_detail?.id) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-white relative">
        <header
          className="absolute top-5 left-5 flex items-center"
          onClick={handle_back}
        >
          <img src={ImageLinks.chevron_left} />
          <button className="font-custom text-sm font-semibold ml-2">
            {t("all_orders")}
          </button>
        </header>

        <p className="text-gray-700 text-sm font-medium mb-4">
          {t("order_details_not_available")}
        </p>
        <button
          onClick={handle_back}
          className="px-4 py-2 text-sm bg-primary text-white rounded-md"
        >
          {t("go_back")}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="h-screen flex flex-col">
        <div className={`${is_pre_pickup ? "flex-[0.55]" : "flex-1"} relative`}>
          {!_.isEmpty(order_detail) && (
            <MapContainer
              className="h-full w-full"
              center={[markers?.marker2?.lat, markers?.marker2?.lng]}
              zoom={15}
              zoomControl={false}
              scrollWheelZoom={true}
              attributionControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
              <FitBounds
                pickupCoord={markers?.marker1}
                dropCoord={markers?.marker2}
              />
              {is_show_rider_icon && (
                <Marker
                  position={[markers?.marker1?.lat, markers?.marker1?.lng]}
                  icon={icon_rider}
                />
              )}
              {!is_show_rider_icon && (
                <Marker
                  position={[markers?.marker1?.lat, markers?.marker1?.lng]}
                  icon={icon_pick}
                />
              )}

              <Marker
                position={[markers?.marker2?.lat, markers?.marker2?.lng]}
                icon={icon_drop}
              />
            </MapContainer>
          )}

          <div className="absolute top-5 left-5" style={{ zIndex: 400 }}>
            <img
              src={ImageLinks.arrow_left_fillef_black}
              onClick={handle_back}
            />
          </div>
          {is_pre_pickup && (
            <div
              className="dropdown dropdown-bottom dropdown-end absolute top-5 right-5"
              style={{ zIndex: 400 }}
            >
              <img src={ImageLinks.menu_dots} tabIndex={0} role="button" />
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[1] w-48 p-2 shadow"
              >
                {/* <li>
                <div
                  className="flex flex-row"
                  onClick={() =>
                    navigate("/create-ticket", { state: { cdr_id: order_id } })
                  }
                  style={{ height: 36 }}
                >
                  <img src={ImageLinks.question_mark_circle} />
                  <p className="font-custom font-medium text-xs">Need Help?</p>
                </div>
              </li> */}
                <li>
                  <div
                    className="flex flex-row"
                    onClick={handle_cancel}
                    style={{ height: 36 }}
                  >
                    <img src={ImageLinks.delete_red} />
                    <p className="font-custom font-medium text-xs text-error">
                      {t("cancel_order")}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          )}
          <div
            className="absolute bottom-5 flex justify-between items-center mb-4 w-11/12 mx-4"
            style={{ zIndex: 400 }}
          >
            {!_.isEmpty(order_detail?.dd_channel?.order_id) && (
              <div
                className="h-9 rounded-lg flex items-center gap-1 bg-white px-2"
                onClick={() => {
                  set_ref_copied(true);
                  navigator.clipboard.writeText(
                    order_detail?.dd_channel?.order_id,
                  );
                  if (!ref_copied) toast.success(t("ref_id_copied"));
                }}
              >
                <p className="font-medium font-custom text-xs">
                  {t("order_number")} {order_detail?.dd_channel?.order_id}
                </p>
                <img src={ImageLinks.copy} />
              </div>
            )}

            <div
              className="h-9 rounded-lg flex items-center gap-1 bg-white px-2"
              onClick={() => {
                set_awb_copied(true);
                navigator.clipboard.writeText(
                  order_detail?.fulfillment?.channel?.order_id,
                );
                if (!awb_copied) toast.success(t("awb_copied"));
              }}
            >
              <p className="font-medium font-custom text-xs">
                {t("awb_label")} {order_detail?.fulfillment?.channel?.order_id}
              </p>
              <img src={ImageLinks.copy} />
            </div>
          </div>
        </div>

        <div
          className={`${
            is_pre_pickup ? "flex-[0.45]" : "flex-1"
          } flex bg-white rounded-t-3xl p-4 shadow-lg flex-col justify-between pb-10`}
        >
          <div>
            {/* {order_detail?.fulfillment?.status !== "CREATED" && ( */}
            <div
              className={`h-9 ${upper_circle_class?.strip_bg_color} flex items-center justify-center rounded-md mb-4`}
            >
              <p
                className={`font-custom font-medium text-xs ${upper_circle_class?.strip_text_color}`}
              >
                {helper_text}
                {/* {order_detail?.fulfillment?.status == "OUT_FOR_PICKUP" ||
                order_detail?.fulfillment?.status == "REACHED_PICKUP"
                  ? eta_val
                    ? "Rider is arriving to pick up your package in"
                    : "Rider is on the way to pickup order."
                  : order_detail?.fulfillment?.status == "OUT_FOR_DELIVERY" ||
                    order_detail?.fulfillment?.status == "REACHED_DELIVERY" ||
                    order_detail?.fulfillment?.status == "PICKED_UP"
                  ? eta_val
                    ? "Rider will deliver your package in."
                    : "Rider is on the way to deliver order"
                  : "Your package has been delivered successfully!"}{" "}
                {eta_val != "" && (
                  <span className="font-semibolf">
                    {eta_val > 60 ? Math.round(eta_val / 60) : eta_val}
                    {eta_val > 60 ? " hour" : " minutes"}!
                  </span>
                )} */}
              </p>
            </div>
            {/* )} */}
            {/* Details */}
            {view_mode === VIEW_MODE.DETAILS && (
              <div className="space-y-4">
                {(order_detail?.fulfillment?.pickup?.otp ||
                  order_detail?.fulfillment?.drop?.otp) && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src={ImageLinks.time_clock} />
                      <p className="font-custom font-semibold text-sm text-dark_black">
                        {order_detail?.fulfillment?.status ==
                          "OUT_FOR_PICKUP" ||
                        order_detail?.fulfillment?.status == "REACHED_PICKUP"
                          ? t("pickup")
                          : t("drop")}{" "}
                        {t("otp")}
                      </p>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      {_.map(
                        order_detail?.fulfillment?.status == "OUT_FOR_PICKUP" ||
                          order_detail?.fulfillment?.status == "REACHED_PICKUP"
                          ? order_detail?.fulfillment?.pickup?.otp?.split("")
                          : order_detail?.fulfillment?.drop?.otp?.split(""),
                        (digit, index) => (
                          <div
                            key={index}
                            style={{ borderWidth: 1 }}
                            className="border-light_gray px-3 py-1.5 rounded-md font-bold"
                          >
                            <p
                              className="font-custom font-semibold"
                              style={{ fontSize: 10 }}
                            >
                              {digit}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
                {(rider_location?.rider?.name ||
                  order_detail?.fulfillment?.rider?.name) && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src={ImageLinks.poc} />
                      <p className="font-custom font-semibold text-sm text-dark_black">
                        {rider_location?.rider?.name ||
                          order_detail?.fulfillment?.rider?.name}
                      </p>
                    </div>

                    {(rider_location?.rider?.mobile ||
                      order_detail?.fulfillment?.rider?.mobile) && (
                      <a
                        href={`tel:+91-${
                          rider_location?.rider?.mobile ||
                          order_detail?.fulfillment?.rider?.mobile
                        }`}
                      >
                        <CallCircle />
                      </a>
                    )}
                  </div>
                )}

                <div className="flex flex-col items-start w-full">
                  <ul className="timeline timeline-vertical w-full">
                    <li className="relative flex pb-5">
                      <div
                        className={`absolute left-1 top-2 h-full w-px ${vertical_line_class}`}
                      ></div>
                      <div
                        className={`absolute top-[8px] h-2 w-2 rounded-full ${upper_circle_class?.color}`}
                      ></div>

                      <div className="pl-5 w-full">
                        <div>
                          <p className="line-clamp-1 font-bold text-sm font-custom text-dark_black">
                            {
                              order_detail?.sender_detail?.address
                                ?.address_line_1
                            }
                          </p>
                          <p className="text-xs text-[#666666] font-custom font-semibold">
                            {
                              order_detail?.sender_detail?.address
                                ?.address_line_2
                            }
                          </p>
                          <p className="text-xs text-[#1E1E1E] font-custom font-medium">
                            {order_detail?.sender_detail?.name}{" "}
                            <span className="mx-2 text-[#C4C4C4]">|</span>
                            {order_detail?.sender_detail?.mobile}
                          </p>
                        </div>
                      </div>
                    </li>

                    <li className="relative flex">
                      <div
                        className={`absolute top-[8px] h-2 w-2 rounded-full ${inner_circle_class}`}
                      ></div>

                      <div className="pl-5 w-full">
                        <div>
                          <p className="font-custom font-bold text-sm text-dark_black">
                            {
                              order_detail?.customer_detail?.address
                                ?.address_line_1
                            }
                          </p>
                          <p className="text-xs text-[#666666] font-custom font-semibold">
                            {
                              order_detail?.customer_detail?.address
                                ?.address_line_2
                            }
                          </p>
                          <p className="text-xs text-[#1E1E1E] font-custom font-semibold">
                            {order_detail?.customer_detail?.name}
                            <span className="mx-2 text-[#]C4C4C4">|</span>
                            {order_detail?.customer_detail?.mobile}
                          </p>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <div className="mt-8 w-full">
                    <div className="flex justify-between items-center">
                      <div className="flex gap-2 items-center w-full">
                        <img
                          src={ImageLinks.receipt}
                          alt="icon"
                          className="mt-[2px]"
                        />
                        <span className="font-custom text-sm font-semibold text-[#1E1E1E]">
                          {t("payment_details")}
                        </span>
                      </div>
                      <div
                        className="flex gap-2 items-center"
                        onClick={() =>
                          set_show_price_breakdown((prev) => !prev)
                        }
                      >
                        <span className="font-custom text-sm font-semibold text-[#1E1E1E]">
                          {formatted_total_amount}
                        </span>
                        <img
                          src={
                            show_price_breakdown
                              ? ImageLinks.chevron_up
                              : ImageLinks.chevron_down
                          }
                          alt="icon"
                          className="mt-[2px]"
                        />
                      </div>
                    </div>
                    {show_price_breakdown && (
                      <div className="mt-2 flex flex-col gap-2 bg-[#FAFAFA] px-[8px] py-[6px]">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-custom text-[#666666] font-medium">
                            {t("delivery_fee")}
                          </span>
                          <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                            {
                              formatCurrencyCountry(
                                delivery_charge?.items?.[0]?.charges
                                  ?.base_charge ??
                                  price_summary?.price_breakup
                                    ?.base_delivery_charge ??
                                  0,
                                currency_code,
                              ).formatedAmount
                            }
                          </span>
                        </div>
                        {(delivery_charge
                          ? (delivery_charge?.items?.[0]?.charges?.rain_surge ||
                              0) +
                            (delivery_charge?.items?.[0]?.charges?.date_surge ||
                              0)
                          : price_summary?.price_breakup?.items?.[0]
                              ?.surge_breakup?.total_surge_base_amount || 0) >
                          0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-custom text-[#666666] font-medium">
                              {t("surge_charge")}
                            </span>
                            <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                              {
                                formatCurrencyCountry(
                                  delivery_charge
                                    ? (delivery_charge?.items?.[0]?.charges
                                        ?.rain_surge || 0) +
                                        (delivery_charge?.items?.[0]?.charges
                                          ?.date_surge || 0)
                                    : price_summary?.price_breakup?.items?.[0]
                                        ?.surge_breakup
                                        ?.total_surge_base_amount || 0,
                                  currency_code,
                                ).formatedAmount
                              }
                            </span>
                          </div>
                        )}
                        {delivery_charge?.items?.[0]?.charges?.waiting_charge >
                          0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-custom text-[#666666] font-medium">
                              {t("waiting_charge")}
                            </span>
                            <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                              {
                                formatCurrencyCountry(
                                  delivery_charge.items[0].charges
                                    .waiting_charge,
                                  currency_code,
                                ).formatedAmount
                              }
                            </span>
                          </div>
                        )}
                        {delivery_charge?.items?.[0]?.charges?.rto_charge >
                          0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-custom text-[#666666] font-medium">
                              {t("rto_charge")}
                            </span>
                            <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                              {
                                formatCurrencyCountry(
                                  delivery_charge.items[0].charges.rto_charge,
                                  currency_code,
                                ).formatedAmount
                              }
                            </span>
                          </div>
                        )}
                        {delivery_charge?.items?.[0]?.charges
                          ?.cancellation_charge > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs font-custom text-[#666666] font-medium">
                              {t("cancellation_charge")}
                            </span>
                            <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                              {
                                formatCurrencyCountry(
                                  delivery_charge.items[0].charges
                                    .cancellation_charge,
                                  currency_code,
                                ).formatedAmount
                              }
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-custom text-[#666666] font-medium">
                            {t("platform_fee")}
                          </span>
                          <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                            {
                              formatCurrencyCountry(
                                platform_fees ?? 5,
                                currency_code,
                              ).formatedAmount
                            }
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-custom text-[#666666] font-medium">
                            {t("gst_amount")}
                          </span>
                          <span className="font-custom text-xs text-[#1E1E1E] font-bold">
                            {
                              formatCurrencyCountry(
                                delivery_charge?.items?.[0]?.charges
                                  ?.total_tax ??
                                  (platform_fees ?? 5) * 0.18 +
                                    (price_summary?.price_breakup
                                      ?.total_gst_amount || 0) +
                                    (price_summary?.price_breakup?.items?.[0]
                                      ?.surge_breakup?.total_surge_tax_amount ||
                                      0),
                                currency_code,
                              ).formatedAmount
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {view_mode === VIEW_MODE.TIMELINE && (
              <div className="space-y-4">
                <div className="flex flex-col items-start w-full pr-2">
                  <ul className="timeline timeline-vertical w-full">
                    {_.map(timeline, (time_item, time_index) => (
                      <li
                        className={`relative flex pb-4`}
                        key={`time_item_${time_index}`}
                      >
                        {time_index != timeline.length - 1 && (
                          <div
                            className={`absolute left-1 top-2 h-full w-px ${vertical_line_class}`}
                          ></div>
                        )}
                        <div
                          className={`absolute  top-1 h-2 w-2 rounded-full ${upper_circle_class?.color}`}
                        ></div>

                        <div className="pl-5 w-full">
                          <div className="flex justify-between items-center">
                            <p className="font-custom font-bold text-xs text-dark_black">
                              {!_.isEmpty(
                                utils.ondc_status_map(time_item?.status),
                              )
                                ? t(
                                    ONDC_STATUS_TRANSLATION_KEYS[
                                      utils.ondc_status_map(time_item?.status)
                                    ],
                                  )
                                : _.capitalize(
                                    time_item?.status?.replaceAll("_", " "),
                                  )}
                            </p>
                            <p className="font-custom text-xs font-medium text-dark_gray">
                              {convertUTCToTimezone(
                                time_item?.timestamp,
                                tz,
                                "DD-MMM",
                              )}
                              &nbsp;|&nbsp;
                              {convertUTCToTimezone(
                                time_item?.timestamp,
                                tz,
                                "hh:mmA",
                              )}{" "}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div>
            {/* Footer */}
            <div className="flex justify-between items-center mt-6">
              <div>
                <button
                  className={`btn btn-outline ${
                    view_mode === VIEW_MODE.DETAILS
                      ? "border-primary"
                      : "border-[#7953D2]/20"
                  } rounded-full btn-sm`}
                  onClick={() => set_view_mode(VIEW_MODE.DETAILS)}
                >
                  <p
                    className={`font-custom font-semibold ${
                      view_mode === VIEW_MODE.DETAILS
                        ? "text-primary"
                        : "text-[#7953D2]/20"
                    } text-[10px]`}
                  >
                    {t("order_details")}
                  </p>
                </button>

                <button
                  className={`ml-4 btn btn-outline  ${
                    view_mode === VIEW_MODE.TIMELINE
                      ? "border-primary"
                      : "border-[#7953D2]/20"
                  } rounded-full btn-sm`}
                  onClick={() => set_view_mode(VIEW_MODE.TIMELINE)}
                >
                  <p
                    className={`font-custom font-semibold ${
                      view_mode === VIEW_MODE.TIMELINE
                        ? "text-primary"
                        : "text-[#7953D2]/20"
                    } text-[10px]`}
                  >
                    {t("timeline")}
                  </p>
                </button>
              </div>
              <button className="btn btn-ghost" onClick={handleShare}>
                <img src={ImageLinks.share} />
                <p
                  className="font-custom font-semibold"
                  style={{ fontSize: 10 }}
                >
                  {t("share_tracking")}
                </p>
              </button>
            </div>
          </div>
        </div>
        {show_cancel_confirm_modal && (
          <OrderCancelConfirm
            close={() => set_show_cancel_confirm_modal(false)}
            order_id={order_id}
          />
        )}
      </div>
    </>
  );
};

export default OrderTrackDetail;
